import { useEffect, useRef } from 'react';

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

const scheduleIdle = (cb: () => void): (() => void) => {
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    const handle = w.requestIdleCallback(cb, { timeout: 1000 });
    return () => w.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(cb, 300);
  return () => window.clearTimeout(handle);
};

const write = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full / unavailable — scanning must not break because of persistence
  }
};

/**
 * Persist `value` to localStorage off the critical path.
 *
 * `JSON.stringify` + `localStorage.setItem` are both O(n) in the size of the value and both run
 * synchronously on the main thread. Writing on every scan makes each scan O(n) — O(n²) over a
 * session — and the cost lands exactly while the operator is scanning. Scheduling the write on an
 * idle callback collapses a burst of scans into a single write: pending writes are cancelled and
 * re-scheduled, so the amortised cost is one serialisation per idle window instead of one per scan.
 *
 * A write still pending when the screen unmounts is flushed synchronously, so navigating away
 * mid-burst never loses scans.
 */
export const useDeferredStorage = (key: string, value: unknown) => {
  const valueRef = useRef(value);
  const pendingRef = useRef(false);

  useEffect(() => {
    valueRef.current = value;
    pendingRef.current = true;
    return scheduleIdle(() => {
      pendingRef.current = false;
      write(key, value);
    });
  }, [key, value]);

  // Declared second, so on unmount it runs after the scheduler above has been cancelled.
  useEffect(
    () => () => {
      if (pendingRef.current) write(key, valueRef.current);
    },
    [key],
  );
};
